import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { LabelPrefs } from './useMapPrefs';
import { exportMapPng } from './exportMapPng';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';

// Live Google map used on the interactive pages. The report/PDF surfaces use
// StaticNetworkMap instead, since a live map can't be rasterized by
// html2canvas.

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';

// Core NYC framing (Lower Manhattan to the Upper West Side, Brooklyn
// waterfront to the Hudson). Screens outside it are still plotted — they're
// just not what the initial view is fit to.
const CORE = { minLon: -74.02, maxLon: -73.96, minLat: 40.70, maxLat: 40.79 };
const MAX_INITIAL_ZOOM = 15;

const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#d3e1d9' }] },
  { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ gamma: 0.01 }, { lightness: 20 }] },
  { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ saturation: -31 }, { lightness: -33 }, { weight: 2 }, { gamma: 0.8 }] },
  { featureType: 'all', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.country', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.province', elementType: 'labels', stylers: [{ visibility: 'on' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ lightness: 30 }, { saturation: 30 }] },
  { featureType: 'landscape.natural.landcover', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.natural.terrain', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ saturation: 20 }] },
  { featureType: 'poi.attraction', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'labels.text.fill', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'labels.text.stroke', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', elementType: 'labels.text.stroke', stylers: [{ saturation: '10' }, { visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ lightness: 20 }, { saturation: -20 }] },
  { featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ lightness: 10 }, { saturation: -30 }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ saturation: 25 }, { lightness: 25 }] },
  { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.station.bus', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.station.rail', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'all', stylers: [{ lightness: -20 }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ visibility: 'off' }] },
];

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
  styles: MAP_STYLE,
};

const MARKER_SIZE = 14;

interface Pin { lat: number; lon: number; id?: string; name?: string; venue?: string }

interface Props {
  screens: Pin[];
  // Cap the square map's width on wide cards.
  maxWidth?: number;
  // Edit mode: click empty map to place, click a pin to select it.
  onMapClick?: (lat: number, lon: number) => void;
  onPinClick?: (id: string) => void;
  onPinDrag?: (id: string, lat: number, lon: number) => void;
  // Label category toggles.
  labelPrefs?: LabelPrefs;
  selectedId?: string | null;
}

export interface NetworkMapHandle { exportPng: () => Promise<string> }

export const NetworkMap = forwardRef<NetworkMapHandle, Props>(function NetworkMap({ screens, maxWidth, onMapClick, onPinClick, onPinDrag, selectedId, labelPrefs }, ref) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: API_KEY });
  const mapRef = useRef<google.maps.Map | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  const startDrag = (e: React.PointerEvent, id: string, cb: (id: string, lat: number, lon: number) => void, select?: (id: string) => void) => {
    const map = mapRef.current;
    const proj = overlayRef.current?.getProjection();
    if (!map || !proj) return;
    e.preventDefault();
    e.stopPropagation();
    select?.(id);
    map.setOptions({ draggable: false });
    const rect = map.getDiv().getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      const ll = proj.fromContainerPixelToLatLng(new window.google.maps.Point(ev.clientX - rect.left, ev.clientY - rect.top));
      if (ll) cb(id, ll.lat(), ll.lng());
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      map.setOptions({ draggable: true });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const options = useMemo<google.maps.MapOptions>(() => {
    const off = { stylers: [{ visibility: 'off' }] };
    const extra: google.maps.MapTypeStyle[] = [];
    if (labelPrefs && !labelPrefs.streets) extra.push({ featureType: 'road', elementType: 'labels', ...off });
    if (labelPrefs && !labelPrefs.landmarks) extra.push({ featureType: 'poi', elementType: 'labels', ...off }, { featureType: 'transit', elementType: 'labels', ...off });
    if (labelPrefs && !labelPrefs.state) extra.push({ featureType: 'administrative.province', elementType: 'labels', ...off });
    return { ...MAP_OPTIONS, styles: [...MAP_STYLE, ...extra] };
  }, [labelPrefs]);

  const pins = useMemo(() => screens.filter(s => s.lat && s.lon), [screens]);
  const framed = useMemo(() => {
    const core = pins.filter(p => p.lon > CORE.minLon && p.lon < CORE.maxLon && p.lat > CORE.minLat && p.lat < CORE.maxLat);
    return core.length ? core : pins;
  }, [pins]);
  // Re-fit only when the set of framed coordinates actually changes, not on
  // every parent re-render that hands us a new array.
  const fitKey = useMemo(() => framed.map(p => `${p.lat},${p.lon}`).join('|'), [framed]);

  useImperativeHandle(ref, () => ({
    exportPng: () => {
      const map = mapRef.current;
      const c = map?.getCenter();
      if (!map || !c) return Promise.reject(new Error('Map not ready'));
      return exportMapPng({
        apiKey: API_KEY,
        center: { lat: c.lat(), lng: c.lng() },
        zoom: Math.round(map.getZoom() ?? 13),
        width: map.getDiv().clientWidth,
        styles: options.styles as google.maps.MapTypeStyle[],
        pins,
      });
    },
  }), [options, pins]);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !framed.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    framed.forEach(p => bounds.extend({ lat: p.lat, lng: p.lon }));
    map.fitBounds(bounds, 40);
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > MAX_INITIAL_ZOOM) map.setZoom(MAX_INITIAL_ZOOM);
    });
  }, [framed]);

  const onLoad = useCallback((map: google.maps.Map) => { mapRef.current = map; fit(); }, [fit]);
  React.useEffect(() => { if (!onMapClick) fit(); }, [fitKey, !!onMapClick]); // eslint-disable-line react-hooks/exhaustive-deps

  const box: React.CSSProperties = {
    width: '100%', maxWidth, margin: maxWidth ? '0 auto' : undefined, aspectRatio: '1 / 1', borderRadius: 12, overflow: 'hidden', position: 'relative',
  };

  if (!pins.length) return null;

  if (loadError || !isLoaded) {
    return (
      <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface-2)', color: 'var(--fg-muted)', fontSize: 13 }}>
        {loadError ? 'Map failed to load' : 'Loading map…'}
      </div>
    );
  }

  const hovered = hover != null ? pins[hover] : null;

  return (
    <div style={box}>
      <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} options={options} onLoad={onLoad}
        onClick={onMapClick ? e => e.latLng && onMapClick(e.latLng.lat(), e.latLng.lng()) : undefined}>
        {pins.map((p, i) => (
          <OverlayView key={p.id ?? `${p.lat},${p.lon},${i}`} position={{ lat: p.lat, lng: p.lon }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} onLoad={o => { overlayRef.current = o; }}>
            <div
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onPointerDown={onPinDrag && p.id ? e => startDrag(e, p.id!, onPinDrag!, onPinClick) : undefined}
              onClick={onPinClick && p.id ? e => { e.stopPropagation(); onPinClick(p.id!); } : undefined}
              style={{
                width: MARKER_SIZE, height: MARKER_SIZE, boxSizing: 'border-box', borderRadius: '50%', cursor: onPinDrag ? 'grab' : 'pointer', touchAction: 'none',
                background: '#0f6b44', border: '2px solid #fff',
                boxShadow: selectedId && p.id === selectedId
                  ? '0 0 0 3px #fff, 0 0 0 6px #0f6b44'
                  : '0 0 0 3px rgba(15,107,68,0.28), 0 1px 3px rgba(0,0,0,0.35)',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </OverlayView>
        ))}

        {hovered && (hovered.name || hovered.venue) && (
          <OverlayView position={{ lat: hovered.lat, lng: hovered.lon }} mapPaneName={OverlayView.FLOAT_PANE}>
            <div style={{
              position: 'absolute', width: 'max-content', minWidth: 120, boxSizing: 'border-box', transform: 'translate(14px, -50%)', background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 10, padding: '8px 12px', pointerEvents: 'none', fontSize: 12, whiteSpace: 'nowrap',
            }}>
              {hovered.name && <div style={{ fontWeight: 700, color: 'var(--fg-strong)' }}>{hovered.name}</div>}
              {hovered.venue && <div style={{ color: 'var(--fg-muted)', fontSize: 11, marginTop: 2 }}>{hovered.venue}</div>}
            </div>
          </OverlayView>
        )}
      </GoogleMap>
    </div>
  );
});
